import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import React from 'react';
import styled from 'styled-components';

import type { ItemSummary } from '@/types/item/ItemSummary';

import PageTitle from '@/components/PageTitle/PageTitle';
import { Catalog } from '@/types/Catalog';
import { shortFormatDate } from '@/utils/dates';
import { getImageUrl, getItemUrlFromSlug } from '@/utils/urls';

import ReviewedPageHeaderMeta from './ReviewedPageHeaderMeta';

type Props = {
    baseUrl: string;
    catalog: Catalog;
    completedReview: any;
    items: ItemSummary[];
};

const ReviewedPageHeader = (props: Props) => {
    const mapImages = React.useCallback((item: ItemSummary): React.ReactElement => {
        const { catalogId, imageVersion, itemId, photos, sellerId, slugWithLocation, title } = item;
        const imageOptions: any = {
            catalogId,
            height: 240,
            imageNumber: photos?.[0],
            imageVersion,
            itemId,
            sellerId,
        };

        const url = getImageUrl(imageOptions);
        const itemUrl = getItemUrlFromSlug(itemId, slugWithLocation);
        return (
            <ImageWrapper key={itemId}>
                <Link
                    href={itemUrl}
                    key={`${itemId}-header-image`}
                    rel="noopener"
                    target="_blank"
                >
                    <img
                        alt={title}
                        src={url}
                    />
                </Link>
            </ImageWrapper>
        );
    }, []);

    const { baseUrl, catalog, completedReview, items } = props;
    const { saleStartTs, sellerName: name, title } = catalog;
    const startDate = shortFormatDate(saleStartTs);

    return (
        <div>
            <ReviewedPageHeaderMeta baseUrl={baseUrl} />
            <CenteredPageTitle title={<FormattedMessage id="review_page.review_your_auction" />} />
            <div>
                <H2>{name}</H2>
                <BodyPrimary>{title}</BodyPrimary>
                <br />
                {startDate}
            </div>
            <ImagesContainer>{items.map(mapImages)}</ImagesContainer>
            <br />
            <BodyPrimary>
                <FormattedMessage
                    id="review_page.rated_date"
                    values={{
                        date: shortFormatDate(completedReview.ReviewSubmittedTS),
                        name: <b key="review-page-rated-date-name">{name}</b>,
                    }}
                />
            </BodyPrimary>
        </div>
    );
};

export default ReviewedPageHeader;

const CenteredPageTitle = styled(PageTitle)`
    text-align: center;
`;

const ImagesContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 15px;
`;

const ImageWrapper = styled.div`
    height: 120px;
    width: 120px;
    margin-right: 7px;
    border: 1px solid ${({ theme }) => theme.colors.grey300};
    position: relative;

    img {
        max-height: 100%;
        max-width: 100%;
        width: auto;
        height: auto;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
    }
`;
